<template>
    <!-- Vertical: centered icon sitting on a divider line, title below -->
    <div v-if="viewMode === 'vertical'" class="mb-8 flex flex-col items-center text-center">
        <div class="relative flex w-full items-center justify-center">
            <div class="w-64 mx-auto flex items-center justify-center space-x-0">
                <span class="flex-1 h-px divider-line divider-line--start"></span>
                <div v-if="icon"
                    class="relative rounded-2xl border border-app px-3 py-3 text-brand-500">
                    <component :is="icon" class="h-6 w-6" />
                </div>
                <span class="flex-1 h-px divider-line divider-line--end"></span>
            </div>
        </div>

        <h2 class="mt-4 text-2xl md:text-3xl text-header leading-snug" :class="[{
            'font-bold': !prefix
        }]">
            <span v-if="prefix" class="font-bold text-brand-500 mt-2">{{ prefix }}</span>
            {{ title }}
        </h2>
        <p v-if="subtitle" class="mt-1 text-body/80 text-sm">{{ subtitle }}</p>

        <div v-if="hasOptions"
            class="mt-4 inline-flex items-center gap-1 rounded-xl border border-body bg-card/70 p-1">
            <button v-for="opt in options" :key="opt.value" type="button"
                @click="emit('update:modelValue', opt.value)" :class="[
                    'flex items-center justify-center h-8 w-8 rounded-sm transition-colors',
                    modelValue === opt.value ? 'bg-brand-500 text-on-brand' : 'text-body/80 hover:text-header'
                ]" :title="opt.label" :aria-label="opt.label">
                <component :is="opt.icon" class="h-4 w-4" />
            </button>
        </div>

        <slot name="action" />
    </div>

    <!-- Normal: icon + title on the left, switcher on the right -->
    <div v-else class="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div class="flex items-center gap-3 min-w-0 flex-1 md:flex-none">
            <div v-if="icon" class="p-2 rounded-lg bg-brand-500/10 text-brand-500 shrink-0">
                <component :is="icon" class="h-5 w-5" />
            </div>
            <div class="min-w-0">
                <h2 class="text-2xl md:text-3xl text-header leading-snug truncate" :class="[{
                    'font-bold': !prefix
                }]">
                    <span v-if="prefix" class="font-bold text-brand-500 mt-2">{{ prefix }}</span>
                    {{ title }}
                </h2>
                <p v-if="subtitle" class="text-body/80 text-xs sm:text-sm truncate">{{ subtitle }}</p>
            </div>
        </div>

        <!-- Style switcher -->
        <div v-if="hasOptions"
            class="flex items-center gap-1 rounded-xl border border-app bg-card/70 p-1 shrink-0">
            <button v-for="opt in options" :key="opt.value" type="button"
                @click="emit('update:modelValue', opt.value)" :class="[
                    'flex items-center justify-center h-8 w-8 rounded-sm transition-colors',
                    modelValue === opt.value ? 'bg-brand-500 text-on-brand' : 'text-body/80 hover:text-header'
                ]" :title="opt.label" :aria-label="opt.label">
                <component :is="opt.icon" class="h-4 w-4" />
            </button>
        </div>

        <slot name="action" />
    </div>
</template>

<script setup lang="ts" generic="T extends string = string">
import { computed } from 'vue';

interface LayoutOption {
    value: T;
    label: string;
    icon: unknown;
}

type ViewMode = 'normal' | 'vertical'

const props = withDefaults(defineProps<{
    prefix?: string;
    title: string;
    icon?: unknown;
    subtitle?: string;
    modelValue?: T;
    hasOptions?: boolean;
    viewMode?: ViewMode;
    options?: LayoutOption[];
}>(), {
    hasOptions: true,
    viewMode: 'vertical',
});

const emit = defineEmits<{ (e: 'update:modelValue', value: T): void }>();

const hasOptions = computed(() => (props.options?.length ?? 0) > 1 && props.hasOptions);
</script>

<style scoped>
.border-app {
    --tw-bg-opacity: 0.2;
    border-color: rgb(var(--core-body-text) / var(--tw-bg-opacity, 1));
}

.bg-app {
    background-color: rgb(var(--core-app-bg));
}

.divider-line {
    background-color: transparent;
}

/* Fade to transparent at the outer ends, solid toward the icon */
.divider-line--start {
    background-image: linear-gradient(to right,
            rgb(var(--core-body-text) / 0),
            rgb(var(--core-body-text) / 0.22));
}

.divider-line--end {
    background-image: linear-gradient(to left,
            rgb(var(--core-body-text) / 0),
            rgb(var(--core-body-text) / 0.22));
}

.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
